import { css } from '@emotion/react';
import { PageProps,withPrefix } from 'gatsby';
import { ContactSection } from '@/components/general/ContactSection';
import { Layout } from '@/components/general/Layout';
import { PageHeader } from '@/components/general/PageHeader';
import { Seo } from '@/components/general/Seo';
import { PlanContentSection } from '@/components/plan/PlanContentSection';
import { getScreen } from '@/styles/media-query';
import { pagesPath } from '@/lib/pagesPath';
import { ButtonLink } from '@/components/shared/ButtonLink';


const Page: React.FC<PageProps> = ({ location }) => (
  <Layout location={location}>
    <Seo
      title="IPO準備クラウドの料金プラン比較｜準備段階に応じた4つのプランをご用意【Uniforce】"
      description="【Uniforce】IPO準備に最適な4つの料金プラン。スタータープランからフルパッケージまで、企業の準備段階に合わせて選択可能。公認会計士によるサポート付き。まずは無料相談で最適なプランをご提案。"
    />
    <PageHeader
      en="Plan"
      title="料金プラン"
      readText={
        <>
          課題や予算に合わせて選べる全4プラン！
        </>
      }
    />
    <div
      css={css`
        margin-top: 40px;
        padding: 0 30px;

        @media ${getScreen('pc')} {
          margin: 72px 0 64px;
        }
      `}
    >
      <h2
        css={css`
          text-align: center;
          color: #00b3a7;
          margin-bottom: 48px;
          font: normal normal bold 18px/32px 'Noto Sans JP', sans-serif;
          letter-spacing: 1.24px;

          @media ${getScreen('pc')} {
            font: normal normal bold 22px/32px 'Noto Sans JP', sans-serif;
            letter-spacing: 1.52px;
          }

          @media ${getScreen('pc')} {
            margin-bottom: 60px;
          }
        `}
      >
        プラン一覧
      </h2>
      <PlanContentSection />
    </div>
    <PlanCaseSection></PlanCaseSection>
    
    <ContactSection
      title1="Uniforce IPO準備クラウドで"
      title2="「IPOの準備を効率的」"
      title3="に"
      title4="進めませんか？"
      text="IPO準備クラウドの機能詳細や無料デモのご相談はこちら"
    />
  </Layout>
);

export default Page;



const PlanCaseSection: React.FC<{
  className?: string;
}> = ({ className }) => {
  
  return (
    <section
      css={css`
        padding: 88px 30px;

        @media ${getScreen('pc', '未満')} {
          padding: 80px 30px 100px;
        }

      `}
      className={className}
    >
      <h2
        css={css`
          text-align: center;
          color: #00b3a7;
          margin-bottom: 48px;
          font: normal normal bold 18px/32px 'Noto Sans JP', sans-serif;
          letter-spacing: 1.24px;

          @media ${getScreen('pc')} {
            font: normal normal bold 22px/32px 'Noto Sans JP', sans-serif;
            letter-spacing: 1.52px;
          }

          @media ${getScreen('pc')} {
            margin-bottom: 60px;
          }
        `}
      >
        導入事例
      </h2>
      <div>
        <CaseCard1 />
        <CaseCard2 />
      </div>
    </section>
  );
}


export type CaseCardProps = {
  className?: string;
  index: number;
  head: React.ReactNode;
  name: string;
  nameItems: string[];
  nameText: React.ReactNode;
  name2: string;
  name2Items: string[];
  name2Text: React.ReactNode;
  result: React.ReactNode;
};

const PlanCaseCard: React.FC<CaseCardProps> = ({
  className,
  index,
  head,
  name,
  nameItems,
  nameText,
  name2,
  name2Items,
  name2Text,
  result,
}) => {
  return (
    <section
      css={css`
        position: relative;
        z-index: 1;
        width: 100%;
        
        @media ${getScreen('pc')} {
          max-width: 560px;
        }
      `}
      className={className}
    >
      <div
        css={css`
          @media ${getScreen('pc')} {
            width: 100%;
            display: grid;
            grid-template-areas:
              '. .'
              'index name'
              'head head'
              'children children'
              'result result'
              '. .';
            grid-template-columns: 60px 1fr;
            grid-template-rows: 1fr auto auto auto auto 1fr;
            min-height: 500px;
            margin-left: auto;
          }
        `}
      >
        <p
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            font: normal normal bold 28px/51px 'Noto Sans JP', sans-serif;
            letter-spacing: 0px;
            color: #ffffff;
            width: 60px;
            height: 60px;
            margin: auto;
            border-radius: 9999px;
            position: relative;
            border: 1px solid #00b3a7;
            background: #fff;

            @media ${getScreen('pc')} {
              align-self: center;
              grid-area: index;
              margin: 0;
            }

            &::before {
              background: #00b3a7
              linear-gradient(180deg, #00b3a7 0%, #00c9be 100%) 0% 0%
              no-repeat;
              content: '';
              width: 54px;
              height: 54px;
              display: block;
              border-radius: 9999px;
              position: absolute;
              top: 2px;
              left: 2px;
            }
          `}
        >
          <span
            css={css`
              margin-bottom: 2px;
              position: relative;
            `}
          >
            {index}
          </span>
        </p>
        <h3
          css={css`
            font: normal normal bold 20px/32px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.8px;
            color: #00b3a7;

            @media ${getScreen('pc')} {
              grid-area: name;
              font: normal normal bold 24px/36px 'Noto Sans JP', sans-serif;
              letter-spacing: 1.66px;
              margin-left: 12px;
            }
          `}
        >
          {head}
        </h3>
        <div
          css={css`
            border-left: 1px solid #00b3a7;
            padding: 8px 0 20px 16px;
            margin: 12px 0 0 10px;

            @media ${getScreen('pc')} {
              grid-area: children;
              margin: 16px 0 0 28px;
              padding: 8px 0 20px 32px;
              max-width: 440px;
            }
          `}
        >
          <p
            css={css`
              font: normal normal bold 16px/24px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.13px;
              color: #5a5b6a;
              margin-bottom: 12px;
            `}
          >
            {name}
          </p>
          
          <ul
            css={css`
              margin-bottom: 24px;
            `}
          >
            {nameItems.map((item, i) => (
              <li
                key={i}
                css={css`
                  display: flex;
                  align-items: center;
                  font: normal normal normal 14px/28px 'Noto Sans JP',
                    sans-serif;
                  letter-spacing: 0.11px;
                  color: #5a5b6a;
                  
                  em {
                    display: inline-flex;
                    justify-content: center;
                    align-items: center;
                    width: 48px;
                    height: 18px;
                    margin-left: 5px;
                    border-radius: 10px;
                    font: normal normal bold 10px/10px 'Noto Sans JP', sans-serif;
                    color: #ffffff;
                    letter-spacing: 0.8px;
                    background-color: #FE8321;
                  }
                `}
              >
                <i
                  css={css`
                    display: block;
                    width: 7px;
                    height: 12px;
                    margin-right: 7px;
                    margin-left: 14px;
                    background: linear-gradient(
                      180deg,
                      #00b3a7 0%,
                      #00c9be 100%
                    );
                    clip-path: path(
                      'M6.98462 6.00556C6.98401 6.28538 6.92468 6.56195 6.81047 6.81741C6.69627 7.07286 6.52973 7.3015 6.32163 7.48856L1.98763 11.3846C1.72601 11.6236 1.4048 11.7878 1.05777 11.8598C0.710752 11.9318 0.350746 11.9089 0.015625 11.7936L3.07063 6.00556L0.016625 0.205561C0.351695 0.0905626 0.711556 0.0679484 1.05839 0.140095C1.40522 0.212241 1.72621 0.376484 1.98763 0.615561L6.32163 4.51256C6.53098 4.70075 6.69826 4.93101 6.81251 5.18828C6.92676 5.44556 6.98541 5.72406 6.98462 6.00556V6.00556Z'
                    );
                  `}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div
            css={css`
              margin-bottom: 24px;
              font: normal normal normal 14px/26px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.42px;
              color: #5a5b6a;

              @media ${getScreen('pc')} {
                font: normal normal normal 14px/28px 'Noto Sans JP', sans-serif;
              }
            `}
          >
            {nameText}
          </div>
          
          <p
            css={css`
              font: normal normal bold 16px/24px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.13px;
              color: #00B3A7;
              margin-bottom: 12px;
            `}
          >
            {name2}
          </p>
          <ul
            css={css`
              margin-bottom: 24px;
            `}
          >
            {name2Items.map((item, i) => (
              <li
                key={i}
                css={css`
                  display: flex;
                  align-items: center;
                  font: normal normal normal 14px/28px 'Noto Sans JP',
                    sans-serif;
                  letter-spacing: 0.11px;
                  color: #5a5b6a;
                  
                  em {
                    display: inline-flex;
                    justify-content: center;
                    align-items: center;
                    width: 48px;
                    height: 18px;
                    margin-left: 5px;
                    border-radius: 10px;
                    font: normal normal bold 10px/10px 'Noto Sans JP', sans-serif;
                    color: #ffffff;
                    letter-spacing: 0.8px;
                    background-color: #FE8321;
                  }
                `}
              >
                <i
                  css={css`
                    display: block;
                    width: 7px;
                    height: 12px;
                    margin-right: 7px;
                    margin-left: 14px;
                    background: linear-gradient(
                      180deg,
                      #00b3a7 0%,
                      #00c9be 100%
                    );
                    clip-path: path(
                      'M6.98462 6.00556C6.98401 6.28538 6.92468 6.56195 6.81047 6.81741C6.69627 7.07286 6.52973 7.3015 6.32163 7.48856L1.98763 11.3846C1.72601 11.6236 1.4048 11.7878 1.05777 11.8598C0.710752 11.9318 0.350746 11.9089 0.015625 11.7936L3.07063 6.00556L0.016625 0.205561C0.351695 0.0905626 0.711556 0.0679484 1.05839 0.140095C1.40522 0.212241 1.72621 0.376484 1.98763 0.615561L6.32163 4.51256C6.53098 4.70075 6.69826 4.93101 6.81251 5.18828C6.92676 5.44556 6.98541 5.72406 6.98462 6.00556V6.00556Z'
                    );
                  `}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div
            css={css`
              font: normal normal normal 14px/26px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.42px;
              color: #5a5b6a;

              @media ${getScreen('pc')} {
                font: normal normal normal 14px/28px 'Noto Sans JP', sans-serif;
              }
            `}
          >
            {name2Text}
          </div>
        </div>
        <div
          css={css`
            background: #ffffff;
            border: 6px solid #00b3a71c;
            border-radius: 16px;
            padding: 14px 16px 12px;

            @media ${getScreen('pc')} {
              grid-area: result;
              padding: 20px 32px 16px;
              width: 100%;
              max-width: 406px;
            }

            strong {
              font: normal normal bold 20px/29px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.8px;
              color: #00b3a7;

              @media ${getScreen('pc')} {
                font: normal normal bold 22px/36px 'Noto Sans JP', sans-serif;
              }
            }

            span {
              font: normal normal bold 16px/24px 'Noto Sans JP', sans-serif;
              letter-spacing: 1.1px;
              color: #00b3a7;

              @media ${getScreen('pc')} {
                font: normal normal bold 19px/27px 'Noto Sans JP', sans-serif;
              }
            }

            small {
              font: normal normal normal 14px/20px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.56px;
              color: #5a5b6a;

              @media ${getScreen('pc')} {
                font: normal normal normal 14px/20px 'Noto Sans JP', sans-serif;
                letter-spacing: 1.1px;
              }
            }
          `}
        >
          {result}
        </div>
      </div>
    </section>
  );
};

export type CaseCardPlanProps = {
  prefix?: React.ReactNode;
  title: string;
  readText: React.ReactNode;
  feature: React.ReactNode;
  contactTo: string;
  children: React.ReactNode;
  isAdvanced?: boolean;
};

const CaseCardPlan: React.FC<CaseCardPlanProps> = ({
  className,
  as: Tag = 'h3',
  prefix,
  title,
  readText,
  feature,
  contactTo,
  children,
  isAdvanced,
}) => {
  return (
    <section 
      css={css`
        flex-shrink: 0;
        padding-top: 70px;
      `}
    >
      <p 
        css={css`
          margin-bottom: 10px;
          text-align: center;
          font: normal normal bold 16px/24px 'Noto Sans JP', sans-serif;
          color: #2B2C40;
          
          &:before {
            content: '';
            position: relative;
            top: -5px;
            display: inline-block;
            width: 35px;
            height: 2px;
            background-color: #00B3A7;
            
            left: 0;
            transform:rotate(45deg);
          }
          &:after {
            content: '';
            position: relative;
            top: -5px;
            display: inline-block;
            width: 35px;
            height: 2px;
            background-color: #00B3A7;
            
            right: 0;
            transform:rotate(-45deg);
          }
        `}
      >
        こんなときにおすすめのプラン
      </p>
      <div
        css={css`
          background: #fff;
          width: 100%;
          position: relative;
          border: 1px solid #d1d8dd;
          border-radius: 16px;
          padding-bottom: 30px;
          
          
          @media ${getScreen('pc')} {
            max-width: 360px;
            border-top-left-radius: 34px;
            border-top-right-radius: 34px;
          }
        `}
        className={className}
      >
        {prefix}
        <header
          css={css`
            height: 135px;
            position: relative;
            padding-top: 22px;
            padding-left: 10px;
            padding-right: 10px;
            margin-bottom: 30px;
            background: #00c9be;
            border-top-left-radius: 34px;
            border-top-right-radius: 34px;
            border-bottom-left-radius: 415px 65px;
            border-bottom-right-radius: 415px 65px;
            
            @media ${getScreen('pc', '未満')} {
              padding-left: 20px;
              padding-right: 20px;
              border-top-left-radius: 15px;
              border-top-right-radius: 15px;
            }

            ${isAdvanced &&
            css`
              background: linear-gradient(180deg, #00b3a7 0%, #00c9be 100%);
            `}
          `}
        >
          <div
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              height: 100%;
              width: 100%;
              overflow: hidden;
              border-top-left-radius: 15px;
              border-top-right-radius: 15px;
            `}
          />
          <p
            css={css`
              max-width: 322px;
              background: #ffffff;
              border-radius: 8px;
              width: 100%;
              padding: 8px 0;
              margin: auto;
              text-align: center;
              font: normal normal bold 14px/22px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.28px;
              color: #5a5b6a;

              @media ${getScreen('pc', '未満')} {
                padding: 4px 0;
                font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
                letter-spacing: 0.28px;
              }

              strong {
                color: #fe8321;
                font-weight: inherit;
              }
            `}
          >
            {readText}
          </p>
          <Tag
            css={css`
              margin-top: 13px;
              text-align: center;
              font-size: 2.6rem;
              line-height: 1.7em;
              font-weight: bold;
              letter-spacing: 0.52px;
              color: #ffffff;

              @media ${getScreen('pc', '未満')} {
                font-size: 2.2rem;
              }
            `}
          >
            {title}
          </Tag>
        </header>
        <div
          css={css`
            padding: 0 24px;
          `}
        >
          {children}
        </div>
        <div
          css={css`
            margin-top: 24px;
            display: flex;
            justify-content: center;
          `}
        >
          <ButtonLink to={contactTo} variant="plan-contact">
            このプランについて相談する
          </ButtonLink>
        </div>
      </div>
    </section>
  );
};





export const CaseCard1: React.FC<Pick<CaseCardProps, 'className'>> = ({
  className,
}) => {
  return (
    <div
      css={css`
        max-width: 910px;
        margin: auto;
        
        @media ${getScreen('pc')} {
          display: flex;
          justify-content: space-between;
          gap: 10px;
        }
      `}
    >
      <PlanCaseCard
        className={className}
        index={1}
        head={
          <>
            N-5期、IPO準備経験者不在の会社の
            <br />
            通常業務からIPO準備まで<br className="sp" />全面サポート！
          </>
        }
        name="経営課題"
        nameItems={[
          'IPO準備の対応',
        ]}
        nameText={<>
          <p>
            5年後にはIPOを目指すため、経理の内製化やガバナンス構築を徐々に進める必要があったが、知見のある社員や相談先がなく、何から始めていいのか全く見当がつかなかった。
          </p>
        </>}
        name2={<><strong>5年後の上場を目指してスタンダードプランを利用</strong></>}
        name2Items={[
          'IPO準備クラウドを利用しながら情報/ノウハウを残す',
        ]}
        name2Text={<>
          <p>
            IPO全体のスケジュールやタスクをクラウド上で網羅的に把握し、担当コンサルティングに相談しながら準備を進めることができた。
          </p>
        </>}
        result={
          <p>
            <strong>IPO準備の土台を構築できた！</strong>
            <br />
            <small>専門的な業務が増え不安でしたが、伴走支援をしていただけたので心強かったです！</small>
          </p>
        }
      >
      </PlanCaseCard>
      
      <CaseCardPlan
        className={className}
        as="h3"
        title="スタンダードプラン"
        readText={
          <>
            <strong>中長期的</strong>に上場を目指している
          </>
        }
        feature={
          <>
            IPO準備にかかるコストを削減しつつ、
            <br />
            計画的に上場準備を進めたい
          </>
        }
        contactTo={pagesPath.estimate.$url()}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          `}
        >
          <p
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 2px;
              font: normal normal bold 15px/21px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.6px;
              color: #00b3a7;
              text-align: center;
              padding: 10px 0 10px;
              background: #f8fffe;
              border: 1px solid #ade7e4;
              border-radius: 4px;
            `}
          >
            <span>Uniforce IPO準備クラウド<br />全機能の利用</span>
          </p>
          <p
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 2px;
              font: normal normal bold 15px/21px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.6px;
              color: #00b3a7;
              text-align: center;
              padding: 10px 0 10px;
              background: #f8fffe;
              border: 1px solid #ade7e4;
              border-radius: 4px;
              
              small {
                display: block;
                font-size: 12px;
                line-height: 1.0;
                color: #7B7B7B;
              }
            `}
          >
            <span>IPO準備アドバイザーによる<br />徹底サポート<small>（カスタマーサポート）</small></span>
          </p>
        </div>
        <dl
          css={css`
            border: 1px solid #ade7e4;
            border-radius: 4px;
            width: 100%;
            margin-top: 18px;
            background: #f8fffe;
          `}
        >
          <dt
            css={css`
              padding: 11px 20px 9px;
              letter-spacing: 0.28px;
              border-bottom: 1px solid #00b3a7;
              text-align: center;
              font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.28px;
              color: #00b3a7;
            `}
          >
            弊社コンサルによる導入・伴走支援
          </dt>
          <dd
            css={css`
              padding: 6px 32px 6px;
            `}
          >
            <ul
              css={css`
                list-style: none;
                text-align: left;
                max-width: 230px;
                margin: 26px auto 32px;
                margin-left: auto;
                margin-right: auto;
                padding-bottom: 0;
                max-width: 100%;
                padding: 1px 0;
                margin: 0;
              `}
            >
              {[
                '初月の導入サポート',
                <>
                  <p>年4回のサポート</p>
                </>,
              ].map((item, i) => (
                <li
                  css={css`
                    position: relative;
                    text-align: left;
                    padding-inline-start: 1.4em;
                    font: normal normal medium 12px/17px 'Noto Sans JP',
                      sans-serif;
                    letter-spacing: 0.12px;
                    color: #444557;

                    &:before {
                      content: '';
                      position: absolute;
                      top: 10px;
                      left: 0;
                      width: 10px;
                      height: 9px;
                      background-repeat: no-repeat;
                      background-image: url(${withPrefix('/images/list-check-icon.svg')});
                    }
                  `}
                  key={i}
                >
                  {item}
                </li>
              ))}
            </ul>
          </dd>
        </dl>
      </CaseCardPlan>
    </div>
  );
};

export const CaseCard2: React.FC<Pick<CaseCardProps, 'className'>> = ({
  className,
}) => {
  return (
    <div
      css={css`
        max-width: 910px;
        margin: auto;
        margin-top: 80px;
        
        @media ${getScreen('pc')} {
          display: flex;
          justify-content: space-between;
          gap: 10px;
        }
      `}
    >
      <PlanCaseCard
        className={className}
        index={2}
        head={
          <>
            N-3期、IPOの全体像を知りたい会社の
            <br />
            IPO準備を網羅的にサポート！
          </>
        }
        name="経営課題"
        nameItems={[
          'IPO準備の知見の偏り',
        ]}
        nameText={<>
          <p>
            IPO準備経験者はいるものの、IPO準備におけるプロジェクトマネジメントの経験はないため、作業は出来てもIPOの全体像の把握やリスク管理ができていなかった。
          </p>
        </>}
        name2={<><strong>3年後の上場を目指してフルパッケージプランを利用</strong></>}
        name2Items={[
          'IPO準備クラウドを利用し実態を把握する',
        ]}
        name2Text={<>
          <p>
            Excelで管理していた情報を、IPO準備クラウド内に入れ直して抜け漏れを確認したことで、コンサルタントと潜在的な課題の洗い出しができた。
          </p>
        </>}
        result={
          <p>
            <strong>抜け漏れを無くし、コストを削減できた！</strong>
            <br />
            <small>全体の動きを把握できたことで、引き継ぎの手間やコストを減らすことができました！</small>
          </p>
        }
      >
      </PlanCaseCard>
      
      <CaseCardPlan
        className={className}
        as="h3"
        title="フルパッケージプラン"
        readText={
          <>
            <strong>2.3年後の上場</strong>を目指している
          </>
        }
        feature={
          <>
            IPO準備にかかるコストを削減しつつ、
            <br />
            計画的に上場準備を進めたい
          </>
        }
        contactTo={pagesPath.estimate.$url()}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          `}
        >
          <p
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 2px;
              font: normal normal bold 15px/21px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.6px;
              color: #00b3a7;
              text-align: center;
              padding: 10px 0 10px;
              background: #f8fffe;
              border: 1px solid #ade7e4;
              border-radius: 4px;
            `}
          >
            <span>Uniforce IPO準備クラウド<br />全機能の利用</span>
          </p>
          <p
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 2px;
              font: normal normal bold 15px/21px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.6px;
              color: #00b3a7;
              text-align: center;
              padding: 10px 0 10px;
              background: #f8fffe;
              border: 1px solid #ade7e4;
              border-radius: 4px;
              
              small {
                display: block;
                font-size: 12px;
                line-height: 1.0;
                color: #7B7B7B;
              }
            `}
          >
            <span>IPO準備アドバイザーによる<br />徹底サポート<small>（カスタマーサポート）</small></span>
          </p>
        </div>
        <dl
          css={css`
            border: 1px solid #ade7e4;
            border-radius: 4px;
            width: 100%;
            margin-top: 18px;
            background: #f8fffe;
          `}
        >
          <dt
            css={css`
              padding: 11px 20px 9px;
              letter-spacing: 0.28px;
              border-bottom: 1px solid #00b3a7;
              text-align: center;
              font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.28px;
              color: #00b3a7;
            `}
          >
            弊社コンサルによる導入・伴走支援
          </dt>
          <dd
            css={css`
              padding: 6px 32px 6px;
            `}
          >
            <ul
              css={css`
                list-style: none;
                text-align: left;
                max-width: 230px;
                margin: 26px auto 32px;
                margin-left: auto;
                margin-right: auto;
                padding-bottom: 0;
                max-width: 100%;
                padding: 1px 0;
                margin: 0;
              `}
            >
              {[
                '初月の導入サポート',
                <>
                  <p>毎月のサポート</p>
                </>,
              ].map((item, i) => (
                <li
                  css={css`
                    position: relative;
                    text-align: left;
                    padding-inline-start: 1.4em;
                    font: normal normal medium 12px/17px 'Noto Sans JP',
                      sans-serif;
                    letter-spacing: 0.12px;
                    color: #444557;

                    &:before {
                      content: '';
                      position: absolute;
                      top: 10px;
                      left: 0;
                      width: 10px;
                      height: 9px;
                      background-repeat: no-repeat;
                      background-image: url(${withPrefix('/images/list-check-icon.svg')});
                    }
                  `}
                  key={i}
                >
                  {item}
                </li>
              ))}
            </ul>
          </dd>
        </dl>
      </CaseCardPlan>
    </div>
  );
};









const PlanFeatureSection: React.FC<{
  className?: string;
  head?: string;
  cards: React.ReactNode[];
  isAdvanced?: boolean;
}> = ({ className, head, cards, isAdvanced }) => {
  return (
    <section
      css={css`
        padding: 88px 30px;

        @media ${getScreen('pc', '未満')} {
          padding: 80px 30px 100px;
        }

        ${isAdvanced &&
        css`
          background: linear-gradient(
            180deg,
            #f0fcfb 0%,
            #f8fffe 49%,
            #ffffff 100%
          );
        `}
      `}
      className={className}
    >
      <h2
        css={css`
          text-align: center;
          color: #00b3a7;
          margin-bottom: 48px;
          font: normal normal bold 18px/32px 'Noto Sans JP', sans-serif;
          letter-spacing: 1.24px;

          @media ${getScreen('pc')} {
            font: normal normal bold 22px/32px 'Noto Sans JP', sans-serif;
            letter-spacing: 1.52px;
          }

          @media ${getScreen('pc')} {
            margin-bottom: 60px;
          }
        `}
      >
        {head}
      </h2>
      <ul
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          text-align: left;
          gap: 100px;
          width: 100%;
          max-width: 950px;
          margin: 0 auto;

          @media ${getScreen('pc')} {
            flex-direction: row;
            gap: 0;
          }
        `}
      >
        {cards.map((card, i) => (
          <li
            key={i}
            css={css`
              max-width: 250px;
              position: relative;

              @media ${getScreen('pc', '未満')} {
                max-width: 100%;
              }
            `}
          >
            {card}
          </li>
        ))}
      </ul>
    </section>
  );
};

const PlanFeatureCard: React.FC<{
  className?: string;
  imageSrc: string;
  head?: string;
  items?: string[];
  children?: React.ReactNode;
  isAdvanced?: boolean;
}> = ({ className, imageSrc, head, items = [], children, isAdvanced }) => {
  return (
    <div
      css={css`
        position: relative;
        z-index: 1;
      `}
      className={className}
    >
      <div
        css={css`
          width: 250px;
          height: 250px;
          position: relative;
          z-index: -1;
          display: flex;
          align-items: center;
          justify-content: center;

          @media ${getScreen('pc', '未満')} {
            margin-left: auto;
            margin-right: auto;
          }

          &::before {
            content: '';
            position: absolute;
            z-index: -1;
            top: 0;
            left: 0;
            right: 0;
            background: linear-gradient(
              180deg,
              #def6f5 -19.9%,
              #f0fcfb 50.78%,
              #ffffff 98.3%
            );
            opacity: 0.4;
            border-radius: 50%;
            width: 100%;
            height: 100%;

            @media ${getScreen('pc', '未満')} {
              margin-left: auto;
              margin-right: auto;
            }

            ${isAdvanced &&
            css`
              background: linear-gradient(
                180deg,
                #def6f5 -19.9%,
                #f0fcfb 50.78%,
                #ffffff 98.3%
              );
              opacity: 0.8;
            `}
          }
        `}
      >
        <img src={imageSrc} width="250" height="250" alt="" />
      </div>
      <h3
        css={css`
          margin-left: auto;
          margin-right: auto;
          margin-top: -56px;
          position: relative;
          text-align: center;
          color: #00b3a7;
          white-space: pre-line;
          font: normal normal bold 16px/24px 'Noto Sans JP', sans-serif;
          letter-spacing: 0.51px;

          @media ${getScreen('pc')} {
            font: normal normal bold 16px/24px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.51px;
          }
        `}
      >
        {head}
      </h3>
      {children && (
        <p
          css={css`
            margin-top: 16px;
            white-space: pre-wrap;
            text-align: center;

            @media ${getScreen('pc')} {
              margin-top: 12px;
            }

            small {
              font-size: 1rem;
              display: block;
            }
          `}
        >
          {children}
        </p>
      )}
    </div>
  );
};
